import { useGetETSTicketNotificationsCountForChannelSelector } from 'gcs-common/clients/api/entities/tickets/tickets-api-selectors';
import IconComponent from 'gcs-common/components/Icon/Icon';
import QuickOrdering from 'gcs-common/components/QuickOrdering/QuickOrdering';
import { ICON, ICON_COLOR } from 'gcs-common/constants/IconConstants';
import { TOOL_TABS } from 'gcs-common/constants/toolUIFlags';
import { Badge } from 'gcs-common/design-system/ui/badge';
import { SidebarHeader, SidebarTrigger } from 'gcs-common/design-system/ui/sidebar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from 'gcs-common/design-system/ui/tab';
import { getNumOpenTasksForChannel } from 'gcs-common/slices/channel/channelSelectors';
import { getSelectedChannelId } from 'gcs-common/slices/channels/channelsSelectors';
import { getIsAgent } from 'gcs-common/slices/currentUser/currentUserSelectors';
import { getCurrentToolBarTab, getFilteredTabs } from 'gcs-common/slices/uiState/uiStateSelectors';
import { setToolbarTab } from 'gcs-common/slices/uiState/uiStateSlice';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import FavoriteList from '../FavoriteList/FavoriteList';
import FileList from '../FilesList/FileList';
import ProcessSelect from '../ProcessSelect/ProcessSelect';
import TaskList from '../TaskList/TaskList';
import Tickets from '../Tickets/Tickets';
import PushWarning from './PushWarning/PushWarning';
import UnannouncedLogin from './UnannouncedLogin/UnannouncedLogin';

const Tools = () => {
  const selectedChannelId = useSelector(getSelectedChannelId);
  const isAgent = useSelector(getIsAgent);
  const currentTabSelection = useSelector(getCurrentToolBarTab(selectedChannelId));
  const tabs = useSelector(getFilteredTabs(selectedChannelId));
  const numOpenTasks = useSelector(getNumOpenTasksForChannel(selectedChannelId));
  const ticketNotificationsCount = useGetETSTicketNotificationsCountForChannelSelector(selectedChannelId);

  const dispatch = useDispatch();

  const setCurrentTabSelection = (tab) => {
    dispatch(setToolbarTab({ tab }));
  };

  const renderTabContent = useMemo(() => {
    return (
      {
        [TOOL_TABS.TODOS]: <TaskList />,
        [TOOL_TABS.FILES]: <FileList />,
        [TOOL_TABS.GCOP]: <ProcessSelect />,
        [TOOL_TABS.TICKETS]: <Tickets />,
        [TOOL_TABS.ORDERS]: <QuickOrdering />,
        default: <FavoriteList />,
      }[currentTabSelection] || <FavoriteList />
    );
  }, [currentTabSelection]);

  const renderTabs = useMemo(() => {
    return tabs.map((tool) => {
      const { name } = tool;

      let iconName;
      let iconTitle;
      let notificationCount;
      switch (name) {
        case TOOL_TABS.TODOS:
          iconName = ICON.CHECKLIST;
          iconTitle = 'To-Dos';
          notificationCount = numOpenTasks;
          break;
        case TOOL_TABS.FILES:
          iconName = ICON.ATTACH_FILE;
          iconTitle = 'Medien';
          break;
        case TOOL_TABS.GCOP:
          iconName = ICON.CUBE;
          iconTitle = 'ONLINE PLUS Vorgänge';
          break;
        case TOOL_TABS.TICKETS:
          iconName = ICON.ETS;
          iconTitle = 'ETS Tickets';
          notificationCount = ticketNotificationsCount;
          break;
        case TOOL_TABS.ORDERS:
          iconName = ICON.QUICKORDERING;
          iconTitle = 'Schnellbestellungen';
          break;
        default:
          iconName = ICON.STAR;
          iconTitle = 'Favoriten';
      }

      return (
        <TabsTrigger key={name} value={name} onClick={() => setCurrentTabSelection(name)}>
          <IconComponent Icon={iconName} color={ICON_COLOR.DARK_GREY} alt={iconTitle} />
          {!!notificationCount && (
            <Badge variant="solid_error" type="counter">
              {notificationCount}
            </Badge>
          )}
        </TabsTrigger>
      );
    });
  }, [tabs, currentTabSelection, numOpenTasks, ticketNotificationsCount]);

  return (
    <>
      <Tabs value={currentTabSelection} className="flex h-full flex-col">
        <SidebarHeader className="shrink-0">
          <TabsList className="flex-1">{renderTabs}</TabsList>
          <SidebarTrigger className="shrink-0" />
        </SidebarHeader>
        <PushWarning />
        {tabs.map((tool) => (
          <TabsContent
            className={currentTabSelection === tool.name ? 'mt-0 flex grow flex-col overflow-auto' : 'hidden'}
            key={tool.name}
            value={tool.name}
          >
            {renderTabContent}
          </TabsContent>
        ))}
      </Tabs>
      {isAgent && <UnannouncedLogin />}
    </>
  );
};

export default Tools;
